import { ChangeEvent } from "react";
import Webcam from "react-webcam";

import { Client } from "@/Common/Api/Client";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { HandNotePageMode } from "@/Common/Models/UI/Enums";
import * as LocalOperations from "@/Common/Operations/operations.local";
import * as LocalPatientOperations from "@/Common/Operations/operations.patient.local";
import * as ServerOperations from "@/Common/Operations/operations.server";
import { Dispatcher } from "@/Common/Stores/Actions/ActionDispatcher";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { NotePagetextControl } from "./HandNote/NotePageTextControl";

import "./NotePage.css";

interface INotePageProps {
    encounterId: string;
}

enum PencilColor {
    Black = 0,
    White,
    Red,
    Green,
    Blue
}

enum PaintTool {
    Pencil,
    Eraser,
    Filler,
    InsertText,
    WebcamMode
}

class NotePageState {
    public pencilColor: PencilColor;
    public activeTool: PaintTool;

    public cameraMode: "none" | "capture" | "webcam";

    public penSizeOptionsVisible: boolean;
    public backgroundOptionsVisible: boolean;

    public textEntryForm: boolean;

    public images: string[];

    public constructor(init?: Partial<NotePageState>) {
        Object.assign(this, init);
    }

    public static default(): NotePageState {
        return new NotePageState({
            activeTool: PaintTool.Pencil,
            penSizeOptionsVisible: false,
            backgroundOptionsVisible: false,
            images: [],
            pencilColor: PencilColor.Black,
            cameraMode: "none",
            textEntryForm: false
        });
    }
}

interface ISize {
    height: number;
    width: number;
    offsetH?: number,
    offsetW?: number
}

class NotePage extends BaseReactComponent<INotePageProps, NotePageState> {
    private uiModel: HandNotePageMode;
    private editImageId: string;
    private storeChangeEventReference: string;

    private textDragInitDone: boolean;
    private webcamInitDone: boolean;
    private previousLineSize: number;

    private lastTextPosX: number;
    private lastTextPosY: number;

    private customBoard2: DrawingBoard.Board;

    private readonly IMAGE_CANVAS_ID = "background-canvas";
    private readonly DRAW_CANVAS_ID = "drawing-board-canvas";
    private readonly TEXT_DRAG_ELEMENT_ID = "__tdr001";

    private CANVAS_HEIGHT: number;
    private CANVAS_WIDTH: number;

    private webcamElement?: Webcam;
    private inputCaptureElement?: HTMLInputElement;

    private readonly colorCodes = [
        { Color: PencilColor.White, Code: "#ffffff", string: "white" },
        { Color: PencilColor.Black, Code: "#000000", string: "black" },
        { Color: PencilColor.Red, Code: "#ff0000", string: "red" },
        { Color: PencilColor.Green, Code: "#008000", string: "green" },
        { Color: PencilColor.Blue, Code: "#0000ff", string: "blue" }
    ];

    public constructor(props: INotePageProps) {
        super(props);
        this.state = NotePageState.default();

        this.uiModel = HandNotePageMode.New;
        this.textDragInitDone = false;
        this.webcamInitDone = false;
        this.CANVAS_HEIGHT = 0;
        this.CANVAS_WIDTH = 0;
        this.lastTextPosX = 0;
        this.lastTextPosY = 0;
        this.editImageId = "";
    }

    public render(): JSX.Element {
        return (
            <div className="note-control" onClick={() => this.containerClick()}>
                <header>
                    <h1><span className="mb-color">MYLE</span> | {cacheStore.GetString("HandwrittenNotesLabel")}</h1>
                    <div className="color-pickers flex">
                        {this.colorCodes.map(color => (
                            <div
                                key={color.Color}
                                className={`color-picker ${color.string} ${color.Color === this.state.pencilColor ? "active" : ""}`}
                                onClick={() => this.setColor(color.Code, color.Color)}
                            />
                        ))}
                    </div>
                    <div className="drawing-modes">
                        <div className="drawing-tool" onClick={() => this.setDrawingTool(PaintTool.Eraser)}>
                            <span className={`icon eraser ${this.isEraserActive ? "active" : ""}`} />
                        </div>
                        <div className="drawing-tool" onClick={() => this.setDrawingTool(PaintTool.Pencil)}>
                            <span className={`icon pencil ${this.isPencilActive ? "active" : ""}`} />
                        </div>
                        {false && (
                            <div className="drawing-tool" onClick={() => this.setDrawingTool(PaintTool.Filler)}>
                                <span className={`icon filler ${this.isFillerActive ? "active" : ""}`} />
                            </div>
                        )}
                        <div className="drawing-tool" onClick={() => this.insertText()}>
                            <span className={`icon text ${this.isInsertTextActive ? "active" : ""}`} />
                        </div>
                    </div>

                    <div className="size-dropdown">
                        <span onClick={e => this.togglePenSizeOptions(e)} className="icon" />
                        {this.state.penSizeOptionsVisible && (
                            <div className="dropdown-options">
                                <div className="option a" onClick={() => this.setPenSize(1)}><span /></div>
                                <div className="option b" onClick={() => this.setPenSize(3)}><span /></div>
                                <div className="option c" onClick={() => this.setPenSize(5)}><span /></div>
                                <div className="option d" onClick={() => this.setPenSize(8)}><span /></div>
                            </div>
                        )}
                    </div>

                    <div className="drawing-modes">
                        <div className="drawing-tool" onClick={() => this.undo()}>
                            <span className="icon undo" />
                        </div>
                        <div className="drawing-tool" onClick={() => this.redo()}>
                            <span className="icon redo" />
                        </div>
                    </div>

                    <div className={this.state.backgroundOptionsVisible ? "background-dropdown open" : "background-dropdown"}>
                        <a onClick={e => this.toggleBackgrounds(e)}>{cacheStore.GetString("BackgroundsLabel")}</a>
                        {this.state.backgroundOptionsVisible && (
                            <div className="options">
                                <div className="option-collection">
                                    {this.state.images.map(
                                        (name: string, idx: number) => (
                                            <div key={name + idx} className="imc">
                                                <img
                                                    onClick={e => this.setBackground(name, e)}
                                                    src={apiSettings.buildAppUrl("api/imaging/handnoteimage", { auth: true, params: { filename: name, AsThumbnail: true } })}
                                                    alt={name}
                                                />
                                            </div>
                                        )
                                    )}
                                </div>
                            </div>
                        )}
                    </div>

                    {this.state.cameraMode !== "none" && (
                        <div className="drawing-modes">
                            <div className="drawing-tool" onClick={() => this.state.cameraMode === "capture" ? this.inputCaptureElement?.click() : this.runWebcam()}>
                                <span className={`icon camera ${this.isWebcamActive ? "active" : ""}`} />
                            </div>
                            {this.state.cameraMode === "capture" && (
                                <input
                                    style={{ display: "none" }} // hide the input so it can be clicked by the camera icon, this hides the default "choose file" and chosen filename info
                                    ref={instance => this.inputCaptureElement = instance}
                                    type="file"
                                    accept="image/*"
                                    capture="environment"
                                    onChange={e => this.getInputCapturePicture(e)}
                                />
                            )}
                        </div>
                    )}

                    <div className="control-btns">
                        <div className="right-buttons">
                            <button
                                onClick={() => this.clearBackground()}
                                className="btn btn-default push-right-5"
                            >
                                <span>{cacheStore.GetString("ClearBackgroundLabel")}</span>
                            </button>
                            <button
                                onClick={() => this.clearPage(true)}
                                className="btn btn-default push-right-5"
                            >
                                <span>{cacheStore.GetString("ClearLabel")}</span>
                            </button>
                            <button
                                className="btn btn-primary"
                                disabled={this.isWebcamActive}
                                onClick={() => this.saveNote()}
                            >
                                <span>{cacheStore.GetString("SaveButton")}</span>
                            </button>
                        </div>
                    </div>
                </header>

                <div className="container-nt">
                    <div id="drawboard" className="drawboard">
                        <div id="cb02" className="psrel">

                            {this.isWebcamActive && (
                                <div className="webcam-overlay">
                                    <div className="wbcam-container">
                                        <div className="frame-container">
                                            <Webcam
                                                ref={ref => this.webcamElement = ref}
                                                audio={false}
                                                height={480}
                                                screenshotFormat="image/jpeg"
                                                width={640}
                                                videoConstraints={{ facingMode: "environment" }}
                                            />
                                        </div>
                                        <div className="wb-footer">
                                            <span className="glyphicon glyphicon-remove" onClick={() => this.exitCameraMode()} />
                                            <span className="glyphicon glyphicon-camera" onClick={() => this.takeSnapshot()} />
                                        </div>
                                    </div>
                                </div>
                            )}

                            {this.isInsertTextActive && (
                                <div onClick={e => this.onTextAreaClick(e)} className="text-drawing-canvas">
                                    {this.state.textEntryForm && (
                                        <NotePagetextControl
                                            id={this.TEXT_DRAG_ELEMENT_ID}
                                            applyTextCallback={() => this.applyText()}
                                            removeTextCallback={() => this.removeTextForm()}
                                        />
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    public componentDidMount(): void {
        const elem = document.getElementById("drawboard");

        const wh = window.outerHeight === 0 ? window.innerHeight : window.outerHeight;

        const displacement = wh - elem.offsetTop;
        $("#drawboard").css("height", (displacement - 68) + "px");

        this.setCanvas();

        this.customBoard2 = new DrawingBoard.Board("cb02", {
            controls: [],
            size: 2,
            webStorage: "none",
            fillTolerance: 20,
            eraserColor: "transparent",
            background: "",
            enlargeYourContainer: true,
            droppable: true, // try dropping an image on the canvas!
            stretchImg: true // the dropped image can be automatically ugly resized to to take the canvas size
        });

        this.customBoard2.ev.bind("board:imageDropped", (src: string) => {
            const canvas = document.getElementById(this.DRAW_CANVAS_ID) as HTMLCanvasElement;
            const canvasContext: CanvasRenderingContext2D = canvas.getContext("2d");
            const image = new Image();

            image.src = src;
            image.onload = () => {
                this.clearPage();
                let dms = this.scaleImage(this.CANVAS_WIDTH, this.CANVAS_HEIGHT, image.width, image.height);
                this.drawImageOnCanvas(image, dms, canvasContext);
                this.customBoard2.history.stack[this.customBoard2.history.stack.length - 1] = canvas.toDataURL();
            };
        });

        const canvasRootelement = $("#cb02").find(".drawing-board-canvas-wrapper");
        const cx = canvasRootelement.find("canvas");
        cx.attr("id", this.DRAW_CANVAS_ID);
        const h = cx.outerHeight(true);
        const w = cx.outerWidth(true);

        this.CANVAS_HEIGHT = h;
        this.CANVAS_WIDTH = w;

        this.attachDrawingCanvas("background-canvas", "drawing-board-canvas", canvasRootelement, h, w);
        this.previousLineSize = this.customBoard2.ctx.lineWidth;

        $(document).bind("keydown", this.onKeyDown.bind(this));

        this.preloadBackgroundList();

        this.storeChangeEventReference = HandNoteModule.noteStore.AddChangeListener(this.strongChangeEventHandler, this, "note-page");

        const cxElement = cx.get(0) as HTMLCanvasElement;
        this.customBoard2.history.initialize(cxElement.toDataURL("image/png"));
        // we fire this event to notify that window is ready and can accept events over its dispatcher
        const ready = new LocalEvent(LocalOperations.HandNote.Ready);
        const wdw: any = window.opener;
        const dsp: Dispatcher<any> = wdw.MiscDispatcher;
        dsp.dispatch(ready);

        this.hasWebcam();
    }

    private strongChangeEventHandler(): void {
        if (HandNoteModule.noteStore.editNote != null) {
            this.uiModel = HandNotePageMode.Edit;
            const x = HandNoteModule.noteStore.editNote;
            this.editImageId = x.Id;
            const img = new Image() as HTMLImageElement;

            img.src = apiSettings.buildAppUrl(`api/patient/encounter/note/handwritten/fetch/${x.Id}`, { auth: true, noCache: true });
            img.onload = () => {
                const canvas = document.getElementById(this.DRAW_CANVAS_ID) as HTMLCanvasElement;
                const canvasContext: CanvasRenderingContext2D = canvas.getContext("2d");
                this.drawImageOnCanvas(img, { height: this.CANVAS_HEIGHT, width: this.CANVAS_WIDTH }, canvasContext);

                this.customBoard2.history.initialize(canvas.toDataURL("image/png"));
            };
        }
    }

    private attachDrawingCanvas(setElementId: string, setElementClass: string, rootElement: JQuery, height: number, width: number, prepend: boolean = true): void {
        const canvas = document.createElement("canvas");
        canvas.setAttribute("id", setElementId);
        canvas.setAttribute("width", String(width));
        canvas.setAttribute("height", String(height));
        canvas.setAttribute("class", setElementClass);
        $(canvas).css({ width: width + "px", height: height + "px" });
        if (prepend)
            rootElement.prepend(canvas);
        else
            rootElement.append(canvas);
    }

    private setCanvas(): void {
        const rootElem = $("#drawboard");
        const h = rootElem.outerHeight(true);
        // our minimums should be height -> we will set the width as per ratio

        const maxHeight = h;
        let maxWidth = 0;

        maxWidth = Math.round((maxHeight * 1.55));
        $("#cb02").css({ "height": maxHeight + "px", "width": maxWidth + "px" });
        $("#drawboard").css("width", maxWidth + "px");
    }

    public componentWillUnmount(): void {
        HandNoteModule.noteStore.RemoveChangeListener(this.storeChangeEventReference);
        $(document).unbind("keydown");
    }

    private setColor(colorCode: string, color: PencilColor): void {
        const isNotPencilOrInsertText = !this.isPencilActive && !this.isInsertTextActive;
        this.customBoard2.setColor(colorCode);
        if (isNotPencilOrInsertText) {
            this.setDrawingBoardMode(PaintTool.Pencil);
            this.customBoard2.ctx.lineWidth = this.previousLineSize;
        }
        this.mutateState(s => {
            s.pencilColor = color;
            if (isNotPencilOrInsertText)
                s.activeTool = PaintTool.Pencil;
        });
    }

    private setDrawingTool(tool: PaintTool): void {
        this.setDrawingBoardMode(tool);
        if (tool === PaintTool.Eraser) {
            this.previousLineSize = this.customBoard2.ctx.lineWidth;
            this.customBoard2.ctx.lineWidth = 40;
        }
        else {
            this.customBoard2.ctx.lineWidth = this.previousLineSize;
        }
        this.mutateState(s => {
            s.activeTool = tool;
            s.textEntryForm = false;
        });
    }

    private setPenSize(size: number): void {
        this.customBoard2.ctx.lineWidth = size;
    }

    private togglePenSizeOptions(e: React.MouseEvent): void {
        e.stopPropagation();
        this.mutateState(s => {
            s.penSizeOptionsVisible = !this.state.penSizeOptionsVisible;
            s.backgroundOptionsVisible = false;
        });
    }

    private containerClick(): void {
        this.mutateState(s => {
            s.penSizeOptionsVisible = false;
            s.backgroundOptionsVisible = false;
        });
    }

    private undo(): void {
        this.customBoard2.goBackInHistory();
    }

    private redo(): void {
        this.customBoard2.goForthInHistory();
    }

    private onKeyDown(e: JQueryEventObject): void {
        if (e.ctrlKey && e.keyCode === 89)
            this.redo();

        if (e.ctrlKey && e.keyCode === 90)
            this.undo();
    }

    public componentDidUpdate(): void {
        if (this.state.textEntryForm && !this.textDragInitDone) {
            this.textDragInitDone = true;
            this.InitTextDragger();
        }
        if (this.state.textEntryForm && this.textDragInitDone) {
            const color = this.getActiveColor();
            $("#" + this.TEXT_DRAG_ELEMENT_ID).find("textarea").css("color", color);
        }
        if (!this.state.textEntryForm && this.textDragInitDone) {
            this.textDragInitDone = false;
            this.destroyDragger(true);
        }

        if (this.isWebcamActive && !this.webcamInitDone)
            this.webcamInitDone = true;

        if (!this.isWebcamActive && this.webcamInitDone)
            this.webcamInitDone = false;
    }

    private InitTextDragger(): void {
        const opt: JQueryUI.DraggableOptions = {
            handle: "span.glyphicon.glyphicon-move",
            stop: () => {
                this.destroyDragger();
                $("#" + this.TEXT_DRAG_ELEMENT_ID).css({ width: "unset", height: "unset" });
                const pos = $("#" + this.TEXT_DRAG_ELEMENT_ID).position();
                this.lastTextPosX = pos.left;
                this.lastTextPosY = pos.top;
                window.setTimeout(() => this.InitTextDragger(), 50);
            }
        };
        $("#" + this.TEXT_DRAG_ELEMENT_ID)
            .draggable(opt)
            .css({ left: this.lastTextPosX + "px", top: this.lastTextPosY + "px" })
            .find("textarea")
            .css("color", this.getActiveColor());
    }

    private destroyDragger(clearText: boolean = false): void {
        const element = $("#" + this.TEXT_DRAG_ELEMENT_ID);
        element.draggable("destroy");
        if (clearText)
            element.find("textarea").val("");
    }

    private getActiveColor(): string {
        const currentColor = this.colorCodes.firstOrDefault(x => x.Color === this.state.pencilColor);

        if (currentColor)
            return currentColor.Code;

        return "#000000";
    }

    private insertText(): void {
        if (this.state.activeTool !== PaintTool.InsertText) {
            // this is in case we were using eraser before this
            this.setDrawingBoardMode(PaintTool.Pencil);
            this.mutateState(s => s.activeTool = PaintTool.InsertText);
        }
    }

    private toggleBackgrounds(e: React.MouseEvent): void {
        e.preventDefault();
        e.stopPropagation();
        this.mutateState(s => {
            s.backgroundOptionsVisible = !this.state.backgroundOptionsVisible;
            s.penSizeOptionsVisible = false;
        });
    }

    private async preloadBackgroundList(): Promise<void> {
        const response = await Client.postAsync<{ ImagePath: string[] }>(
            ServerOperations.Server.Myle_Imaging_HandNoteImages,
            null
        );

        if (response.isSuccess)
            this.mutateState(s => s.images = response.data.ImagePath);
        else
            console.warn("Failed preloading background list:", response.errors);
    }

    private setBackground(fileName: string, e: React.MouseEvent): void {
        e.stopPropagation();
        e.preventDefault();

        if (this.uiModel === HandNotePageMode.Edit) {
            this.clearPage();
            this.uiModel = HandNotePageMode.EditBackgroundReset;
        }

        const canvas = document.getElementById(this.IMAGE_CANVAS_ID) as HTMLCanvasElement;
        const canvasContext: CanvasRenderingContext2D = canvas.getContext("2d");

        const image = new Image() as HTMLImageElement;
        image.src = apiSettings.buildAppUrl("api/imaging/handnoteimage", { auth: true, params: { filename: fileName } });
        image.onload = () => {
            const dimensions = this.scaleImage(canvasContext.canvas.width, canvasContext.canvas.height, image.width, image.height);
            this.drawImageOnCanvas(image, dimensions, canvasContext);
        };

        this.mutateState(s => s.backgroundOptionsVisible = false);
    }

    private scaleImage(drawingCanvasWidth: number, drawingCanvasHeight: number, imageWidth: number, imageHeight: number): ISize {
        if (imageHeight / drawingCanvasHeight > imageWidth / drawingCanvasWidth) {
            // Resize Height to Canvas and Width to Ratio
            let ajustAlignmentH = 0;
            let finalImgWidth = drawingCanvasHeight * imageWidth / imageHeight;
            if (drawingCanvasWidth > finalImgWidth)
                ajustAlignmentH = (drawingCanvasWidth - finalImgWidth) / 2;

            return {
                height: drawingCanvasHeight,
                width: finalImgWidth,
                offsetW: ajustAlignmentH
            };
        }
        else {
            // Resize Height
            let ajustAlignmentV = 0;
            let finalImgHeight = drawingCanvasWidth * imageHeight / imageWidth;
            if (drawingCanvasHeight > finalImgHeight)
                ajustAlignmentV = (drawingCanvasHeight - finalImgHeight) / 2;

            return {
                height: finalImgHeight,
                width: drawingCanvasWidth,
                offsetH: ajustAlignmentV
            };
        }
    }

    private drawImageOnCanvas(image: HTMLImageElement, dimensions: ISize, canvasContext: CanvasRenderingContext2D): void {
        let tempCanvas = document.createElement("canvas") as HTMLCanvasElement;
        let tocx = tempCanvas.getContext("2d");
        tempCanvas.width = dimensions.width;
        tempCanvas.height = dimensions.height;
        tocx.drawImage(image, 0, 0, dimensions.width, dimensions.height);
        let offsetH = dimensions.offsetH ? dimensions.offsetH : 0;
        let offsetW = dimensions.offsetW ? dimensions.offsetW : 0;
        canvasContext.clearRect(offsetW, offsetH, canvasContext.canvas.width, canvasContext.canvas.height);
        canvasContext.drawImage(tempCanvas, offsetW, offsetH, dimensions.width, dimensions.height);// , 0, 0, dimensions.width, dimensions.height);
    }

    private clearPage(saveHistory: boolean = false): void {
        const canvas = document.getElementById(this.DRAW_CANVAS_ID) as HTMLCanvasElement;
        const canvasContext: CanvasRenderingContext2D = canvas.getContext("2d");
        if (saveHistory) {
            this.customBoard2.history.stack.push(canvas.toDataURL());
            this.customBoard2.history.position++;
        }

        canvasContext.clearRect(0, 0, canvasContext.canvas.width, canvasContext.canvas.height);
    }

    private saveNote(): void {
        const imageCanvas = document.getElementById(this.IMAGE_CANVAS_ID) as HTMLCanvasElement;
        const drawCanvas = document.getElementById(this.DRAW_CANVAS_ID) as HTMLCanvasElement;

        const outputCanvas = document.createElement("canvas") as HTMLCanvasElement;
        $(outputCanvas).css({ "background-color": "#ff2800" });

        outputCanvas.setAttribute("width", String(drawCanvas.width));
        outputCanvas.setAttribute("height", String(drawCanvas.height));

        // we need white background to replace transparent spots
        const ocx = outputCanvas.getContext("2d");
        ocx.fillStyle = "#ffffff";
        ocx.fillRect(0, 0, drawCanvas.width, drawCanvas.height);

        // now we can draw 2 layers
        ocx.drawImage(imageCanvas, 0, 0);
        ocx.drawImage(drawCanvas, 0, 0);
        const img = outputCanvas.toDataURL("image/jpeg");

        const operation = new LocalEvent(LocalPatientOperations.MedicalNote.HandNoteReady);
        operation.AddItem("imageUrl", img);
        if (this.uiModel !== HandNotePageMode.New) {
            operation.AddItem("edit", this.editImageId);
        }
        operation.AddItem("encounterId", this.props.encounterId);
        operation.AddItem("imageId", HandNoteModule.noteStore.editNote ? HandNoteModule.noteStore.editNote.Id : null);
        // dispatch

        const opener: any = window.opener;
        const dsp: Dispatcher<any> = opener.LocalEventDispatcher;
        dsp.dispatch(operation);
        window.close();
    }

    private hasWebcam(onSuccessCallback?: () => void): void {
        onSuccessCallback?.();

        if (this.shouldUseInputCapture()) {
            if (this.state.cameraMode === "none")
                this.mutateState(s => s.cameraMode = "capture");

            onSuccessCallback?.();
            return;
        }

        if (navigator.mediaDevices?.enumerateDevices) {
            navigator.mediaDevices.enumerateDevices()
                .then(devices => {
                    if (devices.any(device => device.kind === "videoinput")) {
                        this.setCameraAvailable(onSuccessCallback);
                    }
                    else {
                        this.setCameraUnavailable();
                    }
                });

            return;
        }

        this.getUserMedia({ video: true })
            .then(mediaStream => {
                this.setCameraAvailable(onSuccessCallback);

                // stop any tracks, we only get the stream to check if a camera exists
                mediaStream?.getTracks()?.forEach(track => track.stop());
            })
            .catch(() => this.setCameraUnavailable());
    }

    private getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream> {
        if (navigator.mediaDevices?.getUserMedia)
            return navigator.mediaDevices.getUserMedia(constraints);

        const nav: any = navigator;
        const legacyGetUserMedia = (nav.getUserMedia || nav.webkitGetUserMedia || nav.mozGetUserMedia || nav.msGetUserMedia);

        return new Promise<MediaStream>(
            (resolve, reject) => {
                if (!legacyGetUserMedia) {
                    reject(new Error("GetUserMedia not supported."));
                }
                else {
                    legacyGetUserMedia(
                        constraints,
                        (mediaStream: MediaStream) => resolve(mediaStream),
                        (reason: Error) => reject(reason)
                    );
                }
            }
        );
    }

    private shouldUseInputCapture(): boolean {
        if (window.CurrentDevice.Android() || window.CurrentDevice.iOS())
            return true;

        const inputElement = document.createElement("input");

        return typeof inputElement.capture !== "undefined";
    }

    private setCameraAvailable(onSuccessCallback?: () => void): void {
        // we have a camera
        if (this.state.cameraMode === "none")
            this.mutateState(s => s.cameraMode = "webcam");

        onSuccessCallback?.();
    }

    private setCameraUnavailable(): void {
        // we don't have a camera
        if (this.state.cameraMode !== "none") {
            // we disconnected it..
            this.setDrawingBoardMode(PaintTool.Pencil);
            this.mutateState(s => {
                s.cameraMode = "none";
                s.activeTool = PaintTool.Pencil;
            });
        }
    }

    private onTextAreaClick(e: React.MouseEvent): void {
        if (!this.state.textEntryForm) {
            let t = e.target as HTMLElement;
            this.lastTextPosX = e.clientX - $(t).offset().left;
            this.lastTextPosY = e.clientY - $(t).offset().top;
            this.mutateState(s => s.textEntryForm = true);
        }
    }

    private removeTextForm(): void {
        if (this.textDragInitDone) {
            this.destroyDragger(true);
            this.textDragInitDone = false;
            this.mutateState(s => s.textEntryForm = false);
        }
    }

    private applyText(): void {
        const dragElement = $("#" + this.TEXT_DRAG_ELEMENT_ID);
        const text: string = dragElement.find("textarea").val();
        const mw = dragElement.width();
        const c = this.getActiveColor();
        this.customBoard2.ctx.fillStyle = c;
        this.customBoard2.ctx.font = "18px Arial";

        let appendLineHeight = 25;

        const paragraphs = text.split("\n");

        paragraphs.forEach(p => {
            const lines = this.getLines(this.customBoard2.ctx, p, mw);
            lines.forEach((line: string) => {
                this.customBoard2.ctx.fillText(line, this.lastTextPosX + 4, this.lastTextPosY + appendLineHeight, mw);
                appendLineHeight += 20;
            });
        });

        let canvas = this.customBoard2.ctx.canvas;
        this.customBoard2.history.stack.push(canvas.toDataURL("image/png"));
        this.customBoard2.history.position++;

        this.removeTextForm();
    }

    // http://stackoverflow.com/questions/2936112/text-wrap-in-a-canvas-element
    // by crazy2be. Thanks.
    private getLines(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
        const words = text.split(" ");
        const lines: string[] = [];
        let currentLine = words[0];

        for (let i = 1; i < words.length; i++) {
            const word = words[i];
            const width = ctx.measureText(currentLine + " " + word).width;
            if (width < maxWidth) {
                currentLine += " " + word;
            }
            else {
                lines.push(currentLine);
                currentLine = word;
            }
        }
        lines.push(currentLine);
        return lines;
    }

    private runWebcam(): void {
        this.hasWebcam(
            () => this.mutateState(
                s => {
                    s.activeTool = PaintTool.WebcamMode;
                    s.textEntryForm = false;
                }
            )
        );
    }

    private takeSnapshot(): void {
        let imageSrc = this.webcamElement.getScreenshot();
        let image = new Image();
        image.src = imageSrc;
        image.onload = () => {
            this.loadImage(image);
            this.exitCameraMode();
        };
    }

    private exitCameraMode(): void {
        this.setDrawingBoardMode(PaintTool.Pencil);
        this.mutateState(s => s.activeTool = PaintTool.Pencil);
    }

    private getInputCapturePicture(e: ChangeEvent<HTMLInputElement>): void {
        if (!e.target.files?.length)
            return;

        const file = e.target.files[0];

        let image = new Image();
        image.src = URL.createObjectURL(file);
        image.onload = () => this.loadImage(image);
    }

    private loadImage(image: HTMLImageElement): void {
        if (this.uiModel === HandNotePageMode.Edit) {
            this.clearPage();
            this.uiModel = HandNotePageMode.EditBackgroundReset;
        }
        let canvas = document.getElementById(this.IMAGE_CANVAS_ID) as HTMLCanvasElement;
        let canvasContext: CanvasRenderingContext2D = canvas.getContext("2d");
        let dms = this.scaleImage(this.CANVAS_WIDTH, this.CANVAS_HEIGHT, image.width, image.height);
        this.drawImageOnCanvas(image, dms, canvasContext);
    }

    private clearBackground(): void {
        const canvas = document.getElementById(this.IMAGE_CANVAS_ID) as HTMLCanvasElement;
        const canvasContext = canvas.getContext("2d");
        canvasContext.clearRect(0, 0, this.CANVAS_WIDTH, this.CANVAS_HEIGHT);
    }

    private setDrawingBoardMode(activeTool: PaintTool): void {
        let toolMode = null;
        switch (activeTool) {
            case PaintTool.Pencil:
                toolMode = "pencil";
                break;
            case PaintTool.Filler:
                toolMode = "filler";
                break;
            case PaintTool.Eraser:
                toolMode = "eraser";
                break;
        }
        if (toolMode)
            this.customBoard2.setMode(toolMode);
    }

    private get isPencilActive(): boolean {
        return this.state.activeTool === PaintTool.Pencil;
    }

    private get isInsertTextActive(): boolean {
        return this.state.activeTool === PaintTool.InsertText;
    }

    private get isEraserActive(): boolean {
        return this.state.activeTool === PaintTool.Eraser;
    }

    private get isWebcamActive(): boolean {
        return this.state.activeTool === PaintTool.WebcamMode;
    }

    private get isFillerActive(): boolean {
        return this.state.activeTool === PaintTool.Filler;
    }
}

export { NotePage };
